'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["use"],
  _excluded2 = ["parentUse"],
  _excluded3 = ["bordered", "children", "className", "fill", "listClassName", "moreTabsRenderer", "onSelectedChange", "panelClassName", "selected", "List", "use", "Wrapper", "wrapperClassName", "hideBorders"];
import classNames from 'classnames';
import { BUTTON_RADIUS, LAYOUT_PADDING_X, TAB_PADDING_X, TAB_PADDING_Y } from 'HubStyleTokens/sizes';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { cloneElement, Children, PureComponent } from 'react';
import styled, { css } from 'styled-components';
import UIButton from '../button/UIButton';
import { memoizedSequence } from '../core/Functions';
import Controllable from '../decorators/Controllable';
import UIDropdown from '../dropdown/UIDropdown';
import HiddenMeasure from '../layout/utils/HiddenMeasure';
import UIList from '../list/UIList';
import UISection from '../section/UISection';
import createChainablePropType from '../utils/propTypes/createChainablePropType';
import { setInputMetrics } from '../utils/Styles';
import { BORDER_COLOR, ENCLOSED_SHADED_SELECTED_BACKGROUND_COLOR } from './TabConstants';
import { USES, getSelectedTabBackgroundColor, isContained } from './utils/tabUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HIDDEN_CLASSNAME_REGEX = /sr-only(-focusable)?/g;
const isTabComponent = child => {
  const propTypes = child && child.type && child.type.propTypes;
  return propTypes && propTypes.tabId && propTypes.title;
};
const isWhitespace = str => typeof str === 'string' && !str.match(/\S/);
const getActiveTabPanel = (selected, childArray) => {
  for (let i = 0; i < childArray.length; i++) {
    const child = childArray[i];
    const {
      children: tabContent,
      tabId
    } = child && child.props ? child.props : {};
    if (tabId === selected) {
      return tabContent;
    }
  }
  return null;
};

/**
 * @param {HTMLElement} el
 * @returns {number} The precise width of the element's bounding box (`clientWidth` is rounded)
 */
const getElementWidth = el => el.getBoundingClientRect().width;
const getAvailableWidth = (parentEl, tabsEl) => {
  const computedStyles = getComputedStyle(parentEl, null);

  // Patch for Firefox < 62: https://bugzilla.mozilla.org/show_bug.cgi?id=1467722
  if (!computedStyles) return Infinity;
  const innerWidth = getElementWidth(parentEl) - parseFloat(computedStyles.paddingLeft, 10) - parseFloat(computedStyles.paddingRight, 10);
  let availableWidth = innerWidth;

  // Special case: If we're in a horizontal flex container, respect our siblings' space.
  if (computedStyles.display === 'flex' && computedStyles.flexDirection === 'row' && computedStyles.flexWrap === 'nowrap') {
    const childNodes = parentEl.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      if (childNodes[i] !== tabsEl) availableWidth -= getElementWidth(childNodes[i]);
    }
  }
  return availableWidth;
};

// `PropTypes.children`, but checks all children are `UITab` nodes
const childrenPropTypeValidator = createChainablePropType((props, propName, componentName) => {
  // Only accept UITab instances as children
  let valid = true;
  Children.forEach(props[propName], child => {
    if (child != null && !isTabComponent(child) && !isWhitespace(child)) {
      valid = false;
    }
  });
  if (!valid) {
    return new Error(`${componentName}: All children must be instances of UITab.`);
  }
  return null;
}, 'node' // TODO: render "`UITab`s" instead, linked to the UITab docs
);
const listRightBorderMixin = css(["border-right:", ";"], ({
  fullWidth
}) => !fullWidth ? `1px solid ${BORDER_COLOR}` : '0');
const enclosedBorderMixin = css(["border:1px solid ", ";", ";"], BORDER_COLOR, listRightBorderMixin);
const removeBorders = side => {
  if (side === 'left') {
    return css(["border-left:0;border-top-left-radius:0;"]);
  } else if (side === 'right') {
    return css(["border-right:0;border-top-right-radius:0;"]);
  } else if (side === 'both') {
    return css(["border-left:0;border-right:0;border-top-left-radius:0;border-top-right-radius:0;"]);
  }
  return null;
};
const StyledList = styled.div.withConfig({
  displayName: "UITabs__StyledList",
  componentId: "on2xe1-0"
})(["display:", ";width:", ";margin-bottom:-1px;border-bottom:1px solid ", ";", ";", ""], ({
  use
}) => isContained(use) ? 'inline-flex' : 'flex', ({
  fullWidth
}) => fullWidth && '100%', ({
  bordered
}) => bordered ? BORDER_COLOR : 'transparent', ({
  use
}) => {
  if (use === 'header') return css(["padding-left:", ";"], LAYOUT_PADDING_X);
  if (use === 'enclosed') return css(["border:1px solid ", ";border-top-left-radius:", ";border-top-right-radius:", ";"], BORDER_COLOR, BUTTON_RADIUS, BUTTON_RADIUS);
  if (use === 'enclosed-shaded') {
    return enclosedBorderMixin;
  }
  if (use === 'toolbar') return listRightBorderMixin;
  return null;
}, ({
  hideBorders
}) => {
  return removeBorders(hideBorders);
});
StyledList.displayName = 'UITabs__StyledUITabsList';
const TabsPanel = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UISection, Object.assign({}, rest));
}).withConfig({
  displayName: "UITabs__TabsPanel",
  componentId: "on2xe1-1"
})(["background-color:", ";padding:16px;border-top:1px solid ", ";"], ({
  use
}) => use === 'enclosed-shaded' && ENCLOSED_SHADED_SELECTED_BACKGROUND_COLOR, ({
  bordered
}) => bordered ? BORDER_COLOR : 'transparent');
const StyledWrapper = styled.div.withConfig({
  displayName: "UITabs__StyledWrapper",
  componentId: "on2xe1-2"
})(["", ";"], ({
  use
}) => isContained(use) ? css(["position:relative;&::before{border-top:1px solid ", ";bottom:-1px;content:'';left:0;position:absolute;width:100%;}"], ({
  bordered
}) => bordered ? BORDER_COLOR : 'transparent') : null);
StyledWrapper.displayName = 'UITabs__StyledUITabsWrapper';
const StyledUITabs = styled.div.withConfig({
  displayName: "UITabs__StyledUITabs",
  componentId: "on2xe1-3"
})(["max-width:100%;", ";"], ({
  use
}) => use === 'header' ? css(["margin-bottom:40px;& > ", "{padding-left:", ";}"], TabsPanel, LAYOUT_PADDING_X) : null);
const ExcessTabsDropdownButton = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded2);
  return /*#__PURE__*/_jsx(UIButton, Object.assign({}, rest));
}).withConfig({
  displayName: "UITabs__ExcessTabsDropdownButton",
  componentId: "on2xe1-4"
})(["&&&{", ";flex-shrink:0;", ";}"], setInputMetrics(TAB_PADDING_Y, TAB_PADDING_X, TAB_PADDING_X, TAB_PADDING_Y, false), ({
  parentUse: use
}) => isContained(use) ? css(["background-color:", ";border-left:1px solid ", ";border-radius:0;"], getSelectedTabBackgroundColor(use), BORDER_COLOR) : null);
const renderExcessTabsDropdown = (moreTabsRenderer, childArray, excessTabCount, use, handleTabClick) => {
  if (moreTabsRenderer == null) return null;
  const excessTabs = childArray.slice(childArray.length - excessTabCount);
  const excessTabListItems = excessTabs.map(child => {
    const {
      onClick
    } = child && child.props ? child.props : {};
    return /*#__PURE__*/cloneElement(child, {
      onClick: memoizedSequence(handleTabClick, onClick),
      active: false,
      children: null,
      role: 'button',
      tooltipPlacement: undefined
    });
  });
  return /*#__PURE__*/cloneElement(moreTabsRenderer({
    tabs: excessTabListItems,
    use
  }), {
    'data-more-tabs': true
  });
};
class UITabs extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      excessTabCount: 0
    };
    this.checkFit = () => {
      const {
        moreTabsRenderer
      } = this.props;
      const {
        excessTabCount
      } = this.state;
      if (moreTabsRenderer == null) {
        if (excessTabCount !== 0) this.setState({
          excessTabCount: 0
        });
        return;
      }
      const measureEl = this._measureEl;
      const outerEl = this._outerEl;
      const parentEl = outerEl ? outerEl.parentElement : null;
      if (measureEl == null || outerEl == null || parentEl == null) {
        return;
      }

      // Tally the total tab width. If it's less than the available width, things are simple!
      const containerWidth = Math.ceil(getAvailableWidth(parentEl, outerEl));
      let totalTabsWidth = 0;
      const measureTabsArr = [...measureEl.querySelectorAll('[data-tab-id]')];
      measureTabsArr.forEach(tabEl => {
        totalTabsWidth += getElementWidth(tabEl);
      });
      if (totalTabsWidth <= containerWidth) {
        if (excessTabCount !== 0) this.setState({
          excessTabCount: 0
        });
        return;
      }

      // At this point, we know we can't fit all the tabs. How many do we relegate to the dropdown?
      // We start with the "More" dropdown and the active tab...
      let visibleContentWidth = getElementWidth(measureEl.querySelector('[data-more-tabs]'));
      const activeTab = measureTabsArr.find(el => el.getAttribute('data-tab-selected') === 'true');
      if (activeTab) visibleContentWidth += getElementWidth(activeTab);

      // ...Then we add as many inactive tabs (from left to right) as we can fit.
      const inactiveTabEls = measureTabsArr.filter(el => el.getAttribute('data-tab-selected') !== 'true');
      let newExcessTabCount = inactiveTabEls.length;
      for (let i = 0; i < inactiveTabEls.length; i++) {
        const tabWidth = getElementWidth(inactiveTabEls[i]);
        if (visibleContentWidth + tabWidth > containerWidth) break;
        visibleContentWidth += tabWidth;
        newExcessTabCount -= 1;
      }
      if (newExcessTabCount !== excessTabCount) {
        this.setState({
          excessTabCount: newExcessTabCount
        });
      }
    };
  }
  componentDidMount() {
    if (this._outerEl && this._outerEl.parentElement && this._measureEl) {
      this._resizeObserver = new ResizeObserver(this.checkFit);
      this._resizeObserver.observe(this._outerEl.parentElement);
      this._resizeObserver.observe(this._measureEl);
    }
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.selected !== this.props.selected) {
      // ⚠️ This condition prevents infinite loops! ⚠️
      if (prevState.excessTabCount === this.state.excessTabCount) {
        this.checkFit();
      }
    }
  }
  componentWillUnmount() {
    this._resizeObserver.disconnect();
  }
  render() {
    const _this$props = this.props,
      {
        bordered,
        children,
        className,
        fill,
        listClassName,
        moreTabsRenderer,
        onSelectedChange,
        panelClassName,
        selected,
        List,
        use,
        Wrapper,
        wrapperClassName,
        hideBorders
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded3);
    const {
      excessTabCount
    } = this.state;
    const childArray = Children.toArray(children).filter(child => child && child.type);
    const classes = classNames(className, 'private-tabs', use === 'header' && 'private-tabs--header');

    // If the selected tab would be in the excess tab dropdown, move it to
    // first position instead.
    if (excessTabCount > 0) {
      for (let i = childArray.length - excessTabCount; i < childArray.length; i++) {
        if (childArray[i] && childArray[i].props.tabId === selected) {
          const firstChild = childArray[0];
          childArray[0] = childArray[i];
          childArray[i] = firstChild;
          break;
        }
      }
    }
    const activeTabPanel = getActiveTabPanel(selected, childArray);
    const componentRole = activeTabPanel ? 'tablist' : 'navigation';
    const excessTabsDropdown = renderExcessTabsDropdown(moreTabsRenderer, childArray, excessTabCount, use, onSelectedChange);
    const tabs = childArray.map((child, i) => {
      const {
        active,
        className: childClassName,
        tabId,
        onClick
      } = child.props ? child.props : {};
      const hidden = i >= childArray.length - excessTabCount;
      const tabRole = activeTabPanel ? 'tab' : 'button';
      return /*#__PURE__*/cloneElement(child, {
        onClick: memoizedSequence(onSelectedChange, onClick),
        active: active != null ? active : tabId === selected,
        'aria-current': !activeTabPanel && tabId === selected ? 'page' : null,
        'aria-selected': activeTabPanel && tabId === selected ? true : null,
        children: null,
        className: classNames(childClassName, hidden && "sr-only sr-only-focusable"),
        role: tabRole,
        use,
        fill
      });
    });
    const measureChildren = tabs.map(tab => {
      return /*#__PURE__*/cloneElement(tab, {
        className: tab.props.className.replace(HIDDEN_CLASSNAME_REGEX, ''),
        tooltip: undefined
      });
    });
    return /*#__PURE__*/_jsxs(StyledUITabs, Object.assign({
      className: classes,
      ref: ref => {
        this._outerEl = ref;
      },
      use: use
    }, rest, {
      children: [/*#__PURE__*/_jsxs(Wrapper, {
        bordered: bordered,
        className: classNames('private-tabs__list__wrapper', wrapperClassName),
        use: use,
        children: [/*#__PURE__*/_jsxs(List, {
          bordered: bordered,
          className: classNames('private-tabs__list', listClassName),
          "data-content": true,
          role: componentRole,
          use: use,
          hideBorders: hideBorders,
          fullWidth: fill,
          children: [tabs, excessTabCount > 0 && excessTabsDropdown]
        }), /*#__PURE__*/_jsx(HiddenMeasure, {
          ref: ref => {
            this._measureEl = ref;
          },
          style: {
            height: 0
          },
          children: /*#__PURE__*/_jsxs(List, {
            bordered: bordered,
            use: use,
            children: [measureChildren, excessTabsDropdown]
          })
        })]
      }), activeTabPanel ? /*#__PURE__*/_jsx(TabsPanel, {
        bordered: bordered,
        className: classNames(panelClassName, 'private-tabs__panel'),
        role: "tabpanel",
        use: use,
        children: activeTabPanel
      }) : null]
    }));
  }
}
UITabs.displayName = 'UITabs';
UITabs.propTypes = {
  bordered: PropTypes.bool.isRequired,
  children: childrenPropTypeValidator,
  fill: PropTypes.bool,
  List: PropTypes.elementType,
  listClassName: PropTypes.string,
  moreTabsRenderer: PropTypes.func,
  onSelectedChange: PropTypes.func.isRequired,
  panelClassName: PropTypes.string,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  use: PropTypes.oneOf(USES),
  Wrapper: PropTypes.elementType,
  wrapperClassName: PropTypes.string,
  hideBorders: PropTypes.oneOf(['left', 'right', 'both'])
};
UITabs.defaultProps = {
  bordered: true,
  fill: false,
  moreTabsRenderer: ({
    tabs,
    use
  }) => /*#__PURE__*/_jsx(UIDropdown, {
    Button: ExcessTabsDropdownButton,
    "aria-hidden": true,
    buttonClassName: "private-tabs__excess-tabs-dropdown",
    buttonText: I18n.text('salesUI.UITabs.moreDropdownLabel'),
    buttonUse: "transparent",
    "data-dropdown-tabs": tabs.length,
    placement: "bottom left",
    responsive: false,
    tabIndex: -1,
    parentUse: use,
    children: /*#__PURE__*/_jsx(UIList, {
      children: tabs
    })
  }),
  List: StyledList,
  Wrapper: StyledWrapper
};
export default Controllable(UITabs, ['selected']);